import { useEffect, useRef } from 'react'
import {
  type FieldError,
  type FieldValues,
  type RegisterOptions,
  useFormContext,
} from 'react-hook-form'
import { useHookFormMask } from 'use-mask-input'

import { getNestedValue, prettify } from '@utils'
import type { FieldComponentPropsType } from '@types'

import { DISABLED_PROPS, useDataAttributes } from './_helpers'
import FieldErrorMessage from './FieldErrorMessage'
import FieldLabel from './FieldLabel'
import VisibilityButton from '../VisibilityButton'

/**
 * Shared Ssn Field Component
 *
 * @requires useForm using react-hook-form
 */
export default function SsnField({
  field,
  registerOptions,
}: FieldComponentPropsType) {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext()
  const containerRef = useRef<HTMLDivElement>(null)
  const getInputEl = () => containerRef.current?.querySelector('input')
  const maskRegister = useHookFormMask<FieldValues, RegisterOptions>(register)
  const { onFocus, onLabelClick } = useDataAttributes(containerRef, field.name)

  const fieldLabel = field.label || prettify(field.name)
  const error = getNestedValue<FieldError>(errors, field.name)
  const value = watch(field.name) as string

  const showVisibilityButton = field.disabled === true && Boolean(value)

  useEffect(() => {
    // set input type to password
    const inputEl = getInputEl()
    if (inputEl && inputEl.value !== '') {
      inputEl.type = 'password'
    }
  }, [])

  return (
    <div
      ref={containerRef}
      data-testid="field"
      data-invalid={error ? 'true' : 'false'}
      className="group w-full"
    >
      <div className="relative">
        <FieldLabel
          label={fieldLabel}
          required={field.required}
          onClick={onLabelClick}
        />
        <input
          {...maskRegister(field.name, '999-99-9999', {
            required: field.required,
            placeholder: '*',
            showMaskOnHover: false,
            autoUnmask: false,
            clearMaskOnLostFocus: true,
            ...registerOptions,
            onBlur: () => {
              const inputEl = getInputEl()
              if (inputEl) {
                inputEl.type = 'password'
              }
            },
          } as Parameters<typeof maskRegister>[2])}
          onFocus={() => {
            const inputEl = getInputEl()
            if (inputEl) {
              inputEl.type = 'text'
            }
            onFocus()
          }}
          autoComplete="off"
          aria-invalid={error ? 'true' : 'false'}
          {...(field.disabled === true ? DISABLED_PROPS : null)}
          className="w-full rounded-md border border-gray-400 p-3 text-sm disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-50 disabled:bg-opacity-50 disabled:text-gray-500 group-data-[invalid=true]:border-red-500 group-data-[invalid=true]:text-red-500"
        />
        {showVisibilityButton && (
          <VisibilityButton
            onClick={isVisible => {
              const inputEl = getInputEl()
              if (inputEl) {
                inputEl.type = isVisible ? 'text' : 'password'
              }
            }}
          />
        )}
      </div>
      <FieldErrorMessage label={fieldLabel} error={error} />
    </div>
  )
}
